import { BasicWindow } from "./BasicWindow";
import { Global } from "../../utils/Global";
import { BasicButton } from "../BasicButton";
import Client from "../..";

interface LevelReportData {
  spinsUsed: number;
  baseMoney: number;
  bonusMoney: number;
  requiredScore: number;
  achievedScore: number;
}

export class LevelReport extends BasicWindow {
  private titleText!: Phaser.GameObjects.BitmapText;
  private levelCompletedText!: Phaser.GameObjects.BitmapText;
  private scoreText!: Phaser.GameObjects.BitmapText;
  private requiredScoreText!: Phaser.GameObjects.BitmapText;
  private spinsUsedText!: Phaser.GameObjects.BitmapText;
  private moneyRewardText!: Phaser.GameObjects.BitmapText;
  private bonusMoneyText!: Phaser.GameObjects.BitmapText;
  private actionButton!: BasicButton;

  private onActionCallback?: () => void;

  constructor(scene: Phaser.Scene) {
    super(
      scene,
      0, // x
      0, // y
      400, // width
      300, // height
      "Button1", // textureKey
      "", // frameKey
      400, // sliceWidth
      300, // sliceHeight
      7, // leftWidth
      7, // topHeight
      7, // rightWidth
      7, // bottomHeight
    );

    this.createContent();
    this.setDepth(20000); // Above all UI except pause menu
  }

  private createContent(): void {
    const contentContainer = this.getContentContainer();
    const fontSize = Global.minFontSize;
    const spacing = fontSize * 0.8;

    let currentY = -120; // Start from top

    // Title
    this.titleText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      currentY,
      "fnt_paintBasic",
      "LEVEL COMPLETE!",
      fontSize * 1.5,
    ).setOrigin(0.5);
    contentContainer.add(this.titleText);
    currentY += fontSize * 1.5 + spacing;

    // Level completed text
    this.levelCompletedText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      currentY,
      "fnt_paintBasic",
      "Level 1 Completed",
      fontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.levelCompletedText);
    currentY += fontSize + spacing;

    // Score achieved
    this.scoreText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      currentY,
      "fnt_paintBasic",
      "Score: 0",
      fontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.scoreText);
    currentY += fontSize + spacing;

    // Required score
    this.requiredScoreText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      currentY,
      "fnt_paintBasic",
      "Required: 0",
      fontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.requiredScoreText);
    currentY += fontSize + spacing;

    // Spins used
    this.spinsUsedText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      currentY,
      "fnt_paintBasic",
      "Spins Used: 0/0",
      fontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.spinsUsedText);
    currentY += fontSize + spacing;

    // Money reward
    this.moneyRewardText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      currentY,
      "fnt_paintBasic",
      "Base Reward: $0",
      fontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.moneyRewardText);
    currentY += fontSize + spacing;

    // Bonus money
    this.bonusMoneyText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      currentY,
      "fnt_paintBasic",
      "Performance Bonus: $0",
      fontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.bonusMoneyText);
    currentY += fontSize + spacing * 2;

    // Action Button (Continue or Back to Menu)
    this.actionButton = new ActionButton(this.scene, "Continue", () => {
      this.hide(() => {
        if (this.onActionCallback) {
          this.onActionCallback();
        }
      });
    });
    this.actionButton.setPosition(0, currentY);
    contentContainer.add(this.actionButton);
  }

  public showLevelReport(
    levelData: LevelReportData,
    isSuccess: boolean = true,
    onAction?: () => void,
  ): void {
    this.onActionCallback = onAction;

    // Update the content with current game state
    this.updateContent(levelData, isSuccess);

    // Center the window on screen
    const x = this.scene.scale.width / 2;
    const y = this.scene.scale.height / 2;
    this.setPosition(x, y);

    this.show();
  }

  private updateContent(
    levelData: LevelReportData,
    isSuccess: boolean = true,
  ): void {
    this.scoreText.setText(`Score: ${levelData.achievedScore}`);
    this.requiredScoreText.setText(`Required: ${levelData.requiredScore}`);
    this.spinsUsedText.setText(
      `Spins Used: ${levelData.spinsUsed}/${Client.state.maxNumberOfSpins}`,
    );

    if (isSuccess) {
      const completedLevel = Client.state.level - 1; // Level was already incremented in onLevelSuccess

      if (Client.state.level > Client.state.timeline.length) {
        // Game completed case
        this.titleText.setText("GAME COMPLETED!!!");
        this.levelCompletedText.setText(
          `All ${Client.state.timeline.length} Levels Completed!`,
        );
        this.actionButton.SetText("Back to Menu");

        // Hide money reward texts for game completion
        this.moneyRewardText.setVisible(false);
        this.bonusMoneyText.setVisible(false);
      } else {
        // Regular level completed case
        this.titleText.setText("LEVEL COMPLETE!");
        this.levelCompletedText.setText(`Level ${completedLevel} Completed`);
        this.actionButton.SetText("Continue");

        // Show money reward texts for normal level completion
        this.moneyRewardText.setVisible(true);
        this.bonusMoneyText.setVisible(true);

        // Update money information
        this.moneyRewardText.setText(`Base Reward: $${levelData.baseMoney}`);
        if (levelData.bonusMoney > 0) {
          if (levelData.bonusMoney === 5) {
            this.bonusMoneyText.setText(
              `Performance Bonus: $${levelData.bonusMoney} (max)`,
            );
          } else {
            this.bonusMoneyText.setText(
              `Performance Bonus: $${levelData.bonusMoney}`,
            );
          }
          this.bonusMoneyText.setTint(0x8de35d); // Green for bonus
        } else {
          this.bonusMoneyText.setText(`No Performance Bonus`);
          this.bonusMoneyText.clearTint();
        }
      }
    } else {
      // Failure case
      const currentLevel = Client.state.level;

      this.titleText.setText("YOU LOST");
      this.levelCompletedText.setText(`Made it to Level ${currentLevel}`);
      this.actionButton.SetText("Back to Menu");

      // Hide money reward texts for failure
      this.moneyRewardText.setVisible(false);
      this.bonusMoneyText.setVisible(false);

      // Override spins text for failure
      this.spinsUsedText.setText(`All spins used :(`);
    }

    this.actionButton.ResizeToFitText();

    // Resize window to fit the updated content
    this.resizeToFitContent();
  }

  public hideLevelReport(): void {
    this.hide();
  }

  public override destroy(): void {
    this.scene.tweens.killTweensOf(this);
    super.destroy();
  }
}

// Custom action button for the level report (Continue or Back to Menu)
class ActionButton extends BasicButton {
  private onClickCallback?: () => void;

  constructor(scene: Phaser.Scene, text: string, onClickCallback?: () => void) {
    super(
      scene,
      text,
      { textureKey: "Button1" },
      { textureKey: "Button2" },
      { textureKey: "Button1" },
      undefined,
    );
    this.background.setTint(0xac4c3f);
    this.onClickCallback = onClickCallback;
  }

  handlePointerDown(): void {
    // Visual feedback handled by parent class
  }

  handlePointerUp(): void {
    if (this.onClickCallback) {
      this.onClickCallback();
    }
  }

  handlePointerOver(): void {
    // Visual feedback handled by parent class
  }

  handlePointerOut(): void {
    // Visual feedback handled by parent class
  }
}
